import type {
  AverageLimit,
  AveragePlayerScore,
  AverageScoreTeamMode,
  AverageStat,
  BaseballAverageStatType,
  BasketballAverageStatType,
  Rarity,
  SeasonEligibility,
  So5FixtureEvent,
  So5LeaderboardSeasonality,
} from '#src/__generated__/globalTypes.ts';
import type { AlgoliaCardIndexesName } from '#src/contexts/config/index.ts';

import type { TotalAveragePlayerScore } from './averagePlayerScore.ts';

export enum PlayerSearchViewAttributes {
  AVERAGE_PLAYER_SCORE = 'AVERAGE_PLAYER_SCORE',
  AVERAGE_LIMIT = 'AVERAGE_LIMIT',
  BREAKDOWN = 'BREAKDOWN',
  GAMEPLAY_TIER = 'GAMEPLAY_TIER',
  PROJECTION = 'PROJECTION',
  PROJECTION_GRADE = 'PROJECTION_GRADE',
  STATS = 'STATS',
  SEASONALITY = 'SEASONALITY',
  RARITY = 'RARITY',
  MARKET_VALUE_RARITIES = 'MARKET_VALUE_RARITIES',
  MARKET_VALUE_FILTER_RARITY = 'MARKET_VALUE_FILTER_RARITY',
  PRICE_DISPLAY = 'PRICE_DISPLAY',
  STATS_DISPLAY = 'STATS_DISPLAY',
  SORT = 'SORT',
  ONBOARDING = 'ONBOARDING',
  SHOW_APPEARANCES = 'SHOW_APPEARANCES',
  CARDS_SORT = 'MARKET_DRAWER_SORT',
  STATS_MODE = 'STATS_MODE',
  TEAM_MODE_OVERRIDE = 'TEAM_MODE_OVERRIDE',
}

export type TeamModeOverride = 'AUTO' | AverageScoreTeamMode;

export enum StatsOnboarding {
  INIT = 'INIT',
  ADVANCED_VIEW = 'ADVANCED_VIEW',
  SORT_BY_CLICKING = 'SORT_BY_CLICKING',
  COMBINE_SORTS = 'COMBINE_SORTS',
  FILTERS = 'FILTERS',
  ONBOARDED = 'ONBOARDED',
}

export enum StatsDisplay {
  PER_MATCH = 'PER_MATCH',
  TOTAL = 'TOTAL',
}

export enum PriceDisplay {
  MARKET_VALUE = 'MARKET_VALUE',
  FLOOR_PRICE = 'FLOOR_PRICE',
}

export type StatsRarity =
  | typeof Rarity.limited
  | typeof Rarity.rare
  | typeof Rarity.super_rare
  | typeof Rarity.unique;

export enum StatsMode {
  PITCHING = 'PITCHING',
  HITTING = 'HITTING',
  DEFAULT = 'DEFAULT',
}

export type PlayerSearchView = {
  [PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE]: TotalAveragePlayerScore[];
  [PlayerSearchViewAttributes.BREAKDOWN]: AveragePlayerScore[];
  [PlayerSearchViewAttributes.GAMEPLAY_TIER]: boolean;
  [PlayerSearchViewAttributes.PROJECTION]: So5FixtureEvent[];
  [PlayerSearchViewAttributes.PROJECTION_GRADE]: So5FixtureEvent[];
  [PlayerSearchViewAttributes.STATS]:
    | AverageStat[]
    | BasketballAverageStatType[]
    | BaseballAverageStatType[];
  [PlayerSearchViewAttributes.STATS_MODE]: StatsMode;
  [PlayerSearchViewAttributes.AVERAGE_LIMIT]: AverageLimit;
  [PlayerSearchViewAttributes.RARITY]: StatsRarity;
  [PlayerSearchViewAttributes.MARKET_VALUE_RARITIES]: StatsRarity[];
  [PlayerSearchViewAttributes.MARKET_VALUE_FILTER_RARITY]: StatsRarity;
  [PlayerSearchViewAttributes.PRICE_DISPLAY]: PriceDisplay;
  [PlayerSearchViewAttributes.SEASONALITY]:
    | So5LeaderboardSeasonality
    | SeasonEligibility;
  [PlayerSearchViewAttributes.STATS_DISPLAY]: StatsDisplay;
  [PlayerSearchViewAttributes.SORT]: string;
  [PlayerSearchViewAttributes.SHOW_APPEARANCES]: boolean;
  [PlayerSearchViewAttributes.ONBOARDING]: StatsOnboarding;
  [PlayerSearchViewAttributes.CARDS_SORT]: AlgoliaCardIndexesName;
  [PlayerSearchViewAttributes.TEAM_MODE_OVERRIDE]: TeamModeOverride;
};
